import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const CATS = ["Birth", "Death", "Global Event", "Song Release", "Film Release", "Invention", "Discovery"];
const input = "w-full h-10 rounded-lg border border-slate-300 bg-white px-3 text-sm text-ink focus:outline-none focus:border-gold";
const EMPTY = { date_dd_mm: "", year: "", category: "Global Event", event_title: "", hook: "", description: "", sources: "", source_secondary: "", verified: true, old_style: false };

export const EventFormModal = ({ open, onClose, editing, defaultDate, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setF(editing ? { ...EMPTY, ...editing, year: editing.year ?? "", verified: !!editing.verified, old_style: !!editing.old_style } : { ...EMPTY, date_dd_mm: defaultDate || "" });
  }, [open, editing, defaultDate]);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = { ...f, year: f.year === "" ? null : Number(f.year) };
    delete body.id; delete body.status_label;
    try {
      if (editing) await api.put(`/admin/events/${editing.id}`, body);
      else await api.post("/admin/events", body);
      toast.success(editing ? "Event updated" : "Event added");
      onSaved?.(); onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="event-form-modal">
        <DialogHeader><DialogTitle className="font-display text-ink">{editing ? "Edit event" : "Add event"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <label className="text-xs font-mono text-ink-muted">Date (DD-MM)<input data-testid="event-date" required pattern="\d{2}-\d{2}" placeholder="15-04" value={f.date_dd_mm} onChange={set("date_dd_mm")} className={input} /></label>
            <label className="text-xs font-mono text-ink-muted">Year<input data-testid="event-year" required type="number" value={f.year} onChange={set("year")} className={input} /></label>
            <label className="text-xs font-mono text-ink-muted">Category<select data-testid="event-category" value={f.category} onChange={set("category")} className={input}>{CATS.map((c) => <option key={c}>{c}</option>)}</select></label>
          </div>
          <label className="block text-xs font-mono text-ink-muted">Title<input data-testid="event-title" required value={f.event_title} onChange={set("event_title")} className={input} placeholder="e.g. Kate Winslet born / RMS Titanic sinks" /></label>
          <label className="block text-xs font-mono text-ink-muted">Hook (one-line teaser)<input data-testid="event-hook" value={f.hook || ""} onChange={set("hook")} className={input} placeholder="A forgotten petri dish changed medicine forever." /></label>
          <label className="block text-xs font-mono text-ink-muted">Description (2 sentences)<textarea data-testid="event-description" rows={3} value={f.description} onChange={set("description")} className={input + " h-auto py-2"} /></label>
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs font-mono text-ink-muted">Source (URL)<input data-testid="event-source" value={f.sources} onChange={set("sources")} className={input} /></label>
            <label className="text-xs font-mono text-ink-muted">Secondary source<input value={f.source_secondary} onChange={set("source_secondary")} className={input} /></label>
          </div>
          <label className="flex items-center gap-2 text-sm text-ink"><input data-testid="event-verified" type="checkbox" checked={f.verified} onChange={set("verified")} className="accent-gold w-4 h-4" /> Verified (checked against a source)</label>
          <label className="flex items-center gap-2 text-sm text-ink"><input data-testid="event-old-style" type="checkbox" checked={f.old_style} onChange={set("old_style")} className="accent-gold w-4 h-4" /> Old Style (Julian) date — shows an "O.S. date" badge</label>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 h-10 rounded-full border border-slate-300 text-sm text-ink-muted hover:border-gold">Cancel</button>
            <button data-testid="event-save" type="submit" disabled={saving} className="px-5 h-10 rounded-full bg-ink text-white text-sm font-semibold hover:bg-gold hover:text-ink disabled:opacity-60">{saving ? "Saving…" : "Save"}</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
